import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Dropdown, Spinner } from 'react-bootstrap';
import {
    AlertCircle,
    CheckCircle,
    ChevronDown,
    ChevronLeft,
    ChevronUp,
    FilePlus,
    FileText,
    Landmark,
    Undo2,
    X as XIcon,
} from 'lucide-react';

import { Eyebrow, KebabToggle } from '../Banking/BankhandelserPage';
import FactsPopover from '../Common/FactsPopover';
import KonteringSlip from '../Banking/KonteringSlip';
import BookingEditor from '../Banking/booking/BookingEditor';
import useBookingDraft from '../../../hooks/useBookingDraft';
import { confidenceLabel, confidenceTier } from '../../../services/MatchingService';
import { formatCurrency, formatDate } from '../Banking/bankFormat';

// The three states a skattekonto row can be in, from the list's point of
// view — the same disjoint piles as Bankhändelser: Att bokföra (a booking
// is needed), Att koppla (bokföring exists, the engine suggests the link,
// the user confirms), Klara.
export const TIER_REVIEW = 'review';
export const TIER_MATCH = 'match';
export const TIER_DONE = 'done';

export const hasSuggestedLink = (line) => line.reconciliation_status === 'unmatched' && !!line.suggested_verification_id;

export function tierFor(line) {
    if (hasSuggestedLink(line)) return TIER_MATCH;
    if (line.reconciliation_status === 'unmatched') return TIER_REVIEW;
    return TIER_DONE;
}

const slipRowsOf = (verification) => (verification?.transactions || []).map((t) => ({
    account_number: t.account_number,
    account_name: t.account_name,
    debit: t.transaction_type === 'debit' ? t.amount : 0,
    credit: t.transaction_type === 'credit' ? t.amount : 0,
}));

// The verifikat a row is (or may be) linked to: number · date · text, then
// its kontering as the slip.
function VerificationEvidence({ verification }) {
    if (!verification) return null;
    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', color: '#374151', minWidth: 0 }}>
                <FileText size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                <strong>{verification.series}{verification.number}</strong>
                <span style={{ color: '#9ca3af' }}>·</span>
                <span>{formatDate(verification.date)}</span>
                {verification.description && (
                    <>
                        <span style={{ color: '#9ca3af' }}>·</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {verification.description}
                        </span>
                    </>
                )}
            </div>
            <KonteringSlip rows={slipRowsOf(verification)} />
        </div>
    );
}

// One kontering proposal from the ladder (rule pack first, then the
// standard kontering for the transaction type). The card is the proposal;
// the decision is the footer's primary button.
function ProposalCard({ proposal }) {
    const tier = confidenceTier(proposal.confidence);
    const warnings = (proposal.warnings || [])
        .map((w) => (typeof w === 'string' ? w : w?.message || w?.label || null))
        .filter(Boolean);
    return (
        <div style={{ marginBottom: 24 }}>
            <Eyebrow>{proposal.strategy === 'rule' ? 'Regel' : 'Förslag'}</Eyebrow>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.875rem', color: '#1f2937' }}>{proposal.title}</span>
                <Badge bg={tier.variant} style={{ fontSize: '0.6875rem' }} title="Förslagets bedömningspoäng, inte en uppmätt sannolikhet.">
                    {confidenceLabel(proposal.confidence)}
                </Badge>
            </div>
            <KonteringSlip rows={proposal.rows} />
            {warnings.length > 0 && (
                <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: '0.75rem', color: '#b45309' }}>
                    {warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
            )}
        </div>
    );
}

/**
 * Content of the Skattekonto page's side sheet: a fixed header with the
 * transaction, a scrolling body with the verdict, and a footer that holds
 * the one decision plus a ⋮ menu for the rest — the Bankhändelser shape.
 *
 * `booking` is null, or `{ token, proposal?, manual? }`: the body then swaps
 * to the inline booking view (useBookingDraft + BookingEditor) and the
 * footer to Bokför / Tillbaka.
 */
export default function SkattekontoDetail({
    line,
    companyId,
    tier,
    proposals,
    proposalsLoading,
    proposalsError,
    linkedVerification,
    actionBusy,
    actionError,
    onLink,
    onUnlink,
    booking,
    onOpenBooking,
    onBookingBack,
    onBookingSuccess,
    onClose,
    onPrev,
    onNext,
    position,
}) {
    const amount = parseFloat(line.amount);
    const isInflow = amount > 0;

    // ── Inline booking view ──────────────────────────────────────────────
    const draft = useBookingDraft({ companyId, transaction: line, enabled: !!booking });
    const bookingToken = booking?.token;
    useEffect(() => {
        if (!bookingToken) return;
        draft.init({ proposal: booking.proposal || null, manual: !!booking.manual });
        // A new token means "the view just opened"; nothing else re-inits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingToken]);
    const onBook = async () => {
        const data = await draft.submit();
        if (data) onBookingSuccess?.(data);
    };

    // ── Proposal choice ──────────────────────────────────────────────────
    // The ladder already ranks: the first proposal is the answer, the rest
    // fold behind "Visa fler förslag" and become selectable once shown.
    const [showAll, setShowAll] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    useEffect(() => { setShowAll(false); setSelectedId(null); }, [line.statement_id]);
    const visible = showAll ? proposals : proposals.slice(0, 1);
    const hiddenCount = proposals.length - visible.length;
    const keyOf = (p, index) => p.proposal_id || `${p.strategy}-${index}`;
    const selected = visible.find((p, index) => keyOf(p, index) === selectedId) || visible[0] || null;

    const selectableCard = (proposal, index) => {
        const key = keyOf(proposal, index);
        const node = <ProposalCard proposal={proposal} />;
        if (visible.length < 2) return <React.Fragment key={key}>{node}</React.Fragment>;
        const isSelected = selected === proposal;
        const select = () => setSelectedId(key);
        return (
            <div
                key={key}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={select}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(); } }}
                style={{
                    border: `1px solid ${isSelected ? 'var(--primary)' : '#e5e7eb'}`,
                    borderRadius: 10,
                    padding: '14px 16px 0',
                    marginBottom: 12,
                    cursor: 'pointer',
                }}
            >
                {node}
            </div>
        );
    };

    // ── Footer ⋮ menu: everything that is not the decision ──────────────
    const menuItems = [];
    if (tier === TIER_REVIEW && proposals.length > 0) {
        menuItems.push({ key: 'manual', icon: FilePlus, label: 'Skapa verifikat manuellt', onClick: () => onOpenBooking({ manual: true }) });
    } else if (tier === TIER_MATCH) {
        menuItems.push({ key: 'manual', icon: FilePlus, label: 'Bokför manuellt i stället', onClick: () => onOpenBooking({ manual: true }) });
    } else if (tier === TIER_DONE && line.reconciliation_status === 'auto_matched') {
        menuItems.push({ key: 'unlink', icon: Undo2, label: 'Ångra koppling', onClick: onUnlink });
    }

    // Facts behind the info icon: only what the header does not already say.
    const headerDate = formatDate(line.transaction_date);
    const otherDate = (value) => (value && formatDate(value) !== headerDate ? formatDate(value) : null);
    const facts = [
        ['Transaktionsdatum', headerDate],
        ['Bokföringsdatum', otherDate(line.booking_date)],
        ['Period', line.period_reference || null],
        ['OCR', line.ocr_reference || null],
        ['Saldo efter', line.balance != null && line.balance !== '' ? formatCurrency(parseFloat(line.balance)) : null],
    ];

    const busyIcon = <Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2 }} />;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {/* Fixed header zone: the transaction itself. */}
            <div style={{ padding: '16px 24px 22px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                <div className="d-flex align-items-center justify-content-between" style={{ gap: 12, marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <span style={{
                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            background: '#f3f4f6', color: '#6b7280',
                        }}>
                            <Landmark size={16} />
                        </span>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#111827' }}>Skattekonto</div>
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontSize: '0.8125rem', color: '#6b7280', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} title="Transaktionsdatum">
                            {headerDate}
                        </span>
                        <FactsPopover id={`skv-facts-${line.statement_id}`} label="Skatteverkets uppgifter" facts={facts} min={2} />
                    </span>
                </div>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', overflowWrap: 'anywhere' }}>
                    {line.description || '(ingen beskrivning)'}
                </div>
                <div style={{ marginTop: 4 }}>
                    <span style={{ fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.01em', color: isInflow ? '#10b981' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                        {formatCurrency(amount)}
                    </span>
                </div>
            </div>

            {/* Scrolling body: the verdict. The key retriggers the entrance
                animation on every row swap / data arrival. */}
            <div
                key={`${line.statement_id}:${proposalsLoading ? 'loading' : 'ready'}:${booking ? 'book' : 'review'}`}
                className="bank-pane-in ll-sheet-body"
                style={{ padding: '24px 24px 28px', flex: 1, minHeight: 0 }}
            >
                {actionError && !booking && (
                    <Alert variant="danger" className="py-2" style={{ fontSize: '0.8125rem' }}>
                        <AlertCircle size={14} className="me-2" />{actionError}
                    </Alert>
                )}

                {booking && (
                    <>
                        <Eyebrow>Bokför</Eyebrow>
                        <BookingEditor key={bookingToken} companyId={companyId} draft={draft} />
                    </>
                )}

                {!booking && tier === TIER_REVIEW && (
                    <>
                        {proposalsLoading && (
                            <div style={{ padding: '12px 0', color: '#6b7280', fontSize: '0.875rem' }}>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Hämtar förslag…
                            </div>
                        )}
                        {!proposalsLoading && proposalsError && (
                            <Alert variant="warning" className="py-2" style={{ fontSize: '0.8125rem' }}>
                                <AlertCircle size={14} className="me-2" />{proposalsError}
                            </Alert>
                        )}
                        {!proposalsLoading && !proposalsError && proposals.length === 0 && (
                            <>
                                <Eyebrow>Att bokföra</Eyebrow>
                                <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginBottom: 0 }}>
                                    Ingen regel känner igen raden.
                                </p>
                            </>
                        )}
                        {!proposalsLoading && visible.map(selectableCard)}
                        {!proposalsLoading && (hiddenCount > 0 || showAll) && proposals.length > 1 && (
                            <Button variant="link" size="sm" className="text-muted p-0 mb-2" onClick={() => setShowAll((v) => !v)}>
                                {showAll ? (
                                    <><ChevronUp size={14} className="me-1" />Dölj övriga förslag</>
                                ) : (
                                    <><ChevronDown size={14} className="me-1" />Visa {hiddenCount} {hiddenCount === 1 ? 'förslag till' : 'fler förslag'}</>
                                )}
                            </Button>
                        )}
                    </>
                )}

                {!booking && tier === TIER_MATCH && (
                    <>
                        <Eyebrow color="#b45309">Redan bokförd?</Eyebrow>
                        <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 18 }}>
                            Raden ser ut att motsvara verifikatet nedan.
                        </p>
                        <VerificationEvidence verification={linkedVerification} />
                        <div style={{ fontSize: '0.6875rem', color: '#9ca3af' }}>
                            Inget nytt bokförs — raden kopplas till verifikatet.
                        </div>
                    </>
                )}

                {!booking && tier === TIER_DONE && (
                    <>
                        <Eyebrow color="#10b981">Klar</Eyebrow>
                        {line.matched_verification_id ? (
                            <>
                                <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 18 }}>Kopplad till verifikatet nedan.</p>
                                <VerificationEvidence verification={linkedVerification} />
                            </>
                        ) : (
                            <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 0 }}>Avstämd — inget verifikat behövdes.</p>
                        )}
                    </>
                )}
            </div>

            {/* Fixed bottom bar. Left: the decision and the ⋮ menu. Right:
                position | prev/next | close. */}
            <div className="ll-sheet-footer">
                <div className="ll-sheet-controls">
                    {booking && (
                        <>
                            <button type="button" className="ll-sheet-btn ll-sheet-btn--primary" disabled={draft.creating || !draft.canSubmit} onClick={onBook}>
                                {draft.creating ? busyIcon : <CheckCircle size={14} />}
                                Bokför verifikat
                            </button>
                            <button type="button" className="ll-sheet-btn" disabled={draft.creating} onClick={onBookingBack}>
                                <ChevronLeft size={14} />
                                Tillbaka
                            </button>
                        </>
                    )}
                    {!booking && tier === TIER_REVIEW && !proposalsLoading && (
                        <>
                            {selected ? (
                                <button type="button" className="ll-sheet-btn ll-sheet-btn--primary" disabled={actionBusy} onClick={() => onOpenBooking({ proposal: selected })}>
                                    <FilePlus size={14} />
                                    Bokför så här
                                </button>
                            ) : (
                                <button type="button" className="ll-sheet-btn ll-sheet-btn--primary" disabled={actionBusy} onClick={() => onOpenBooking({ manual: true })}>
                                    <FilePlus size={14} />
                                    Skapa verifikat
                                </button>
                            )}
                            {menuItems.length > 0 && <span className="ll-sheet-controls__sep" aria-hidden="true" />}
                        </>
                    )}
                    {!booking && tier === TIER_MATCH && (
                        <>
                            <button type="button" className="ll-sheet-btn ll-sheet-btn--primary" disabled={actionBusy} onClick={onLink}>
                                {actionBusy ? busyIcon : <CheckCircle size={14} />}
                                {actionBusy ? 'Kopplar…' : 'Koppla till verifikatet'}
                            </button>
                            <span className="ll-sheet-controls__sep" aria-hidden="true" />
                        </>
                    )}
                    {!booking && menuItems.length > 0 && (
                        <Dropdown drop="up" align="start">
                            <Dropdown.Toggle as={KebabToggle} disabled={actionBusy} />
                            <Dropdown.Menu>
                                {menuItems.map((item) => (
                                    <Dropdown.Item key={item.key} disabled={item.disabled} onClick={item.onClick}>
                                        {item.icon && <item.icon size={14} />}{item.label}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>
                <div className="ll-sheet-controls">
                    {position && (
                        <>
                            <span className="ll-sheet-controls__count">{position.index} av {position.total}</span>
                            <span className="ll-sheet-controls__sep" aria-hidden="true" />
                        </>
                    )}
                    <div className="ll-sheet-nav" role="group" aria-label="Bläddra mellan transaktioner">
                        <button type="button" onClick={onPrev} disabled={!onPrev || !!booking} aria-label="Föregående transaktion" title="Föregående (↑)">
                            <ChevronUp size={16} />
                        </button>
                        <button type="button" onClick={onNext} disabled={!onNext || !!booking} aria-label="Nästa transaktion" title="Nästa (↓)">
                            <ChevronDown size={16} />
                        </button>
                    </div>
                    <span className="ll-sheet-controls__sep" aria-hidden="true" />
                    <button type="button" className="ll-sheet-close" onClick={onClose} aria-label="Stäng" title="Stäng (Esc)">
                        <XIcon size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}
